import { defineDocument } from '../../schema';
export const gameQuinspireDocument = defineDocument({
    id: 'game:quinspire',
    kind: 'game',
    slug: 'quinspire',
    route: '/discover/games/quinspire',
    seo: {
        title: 'Discover Quinspire',
        description: 'A hangman-style word game with a twist. Guess letters to reveal the five mystery words at once. Correct letters and complete words score points.',
        imageAsset: 'coverQuinspire',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Quinspire',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A word-guessing game where players reveal five themed words at once by guessing letters.',
            },
        ],
        imageAsset: 'coverQuinspire',
        imageAlt: 'Quinspire game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Reach for the clouds in this twist on classic hangman. A player picks a theme, then five mystery words stack into a pyramid. Guess letters one by one to uncover all five words at once.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Correct letters score points. Finish a word for a bonus. Guess wrong? You're locked out while everyone else keeps climbing.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Most points after all rounds wins. Think you can hang?',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1-6 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Selection',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~11 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'December 18, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '693bad642f3a8ff742190144',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: "One player selects the theme for the round. They pick one of Poppy's suggestions or choose their own and type it in.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Five mystery words related to the theme are arranged in a five-level pyramid formation.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players guess letters one at a time to reveal words across all five levels simultaneously. When a letter is guessed correctly, it appears in all positions where it exists across the pyramid. Players who guess incorrectly are temporarily locked out while others continue.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Once time runs out, players are awarded points for the round.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat for four rounds with different themes.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: '1 point for every time a correctly guessed letter appears in a word.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: '1 bonus point for completing a full word.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The player(s) with the highest score at the end of the game wins.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Custom round themes',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Theme suggestions',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Mystery words based on chosen themes',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: low',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: word nerds, small groups, remote groups',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/meet-quinspire-a-five-high-pyramid-scheme',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Quinspire blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '693bad642f3a8ff742190144',
                },
            ],
        },
    ],
});
//# sourceMappingURL=quinspire.js.map